import React, { useState, useEffect, useCallback } from 'react';
import { Search, Filter, Clock, CheckCircle, XCircle, AlertCircle, Eye, MoreVertical } from 'lucide-react';
import { Table } from '../components/ui/Table';
import { driverApi } from '../services/driverApi';

interface Job {
  id: string;
  name: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  created_at: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  result?: any;
}

export const DriverJobs: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    status: 'all'
  });
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.page,
        page_size: pagination.page_size
      };

      if (filters.search) params.search = filters.search;
      if (filters.status !== 'all') params.status = filters.status;

      const response = await driverApi.getJobs(params);
      setJobs(response.data || []);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
        page_length: response.pagination?.page_size || prev.page_size
      }));
    } catch (error) {
      console.error('Failed to load jobs:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.page_size, filters]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeDropdown && !(event.target as Element).closest('.dropdown-container')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  const handleSearch = (search: string) => {
    setFilters(prev => ({ ...prev, search }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      status: 'all'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleViewJob = async (job: Job) => {
    try {
      const details = await driverApi.getJobDetails(job.id);
      setSelectedJob({ ...job, ...details });
      setShowJobDetails(true);
    } catch (error) {
      console.error('Failed to load job details:', error);
    }
  };

  const handleViewDetails = async (job: Job) => {
    await handleViewJob(job);
    setActiveDropdown(null);
  };

  const handleRetryJob = (job: Job) => {
    // TODO: Implement retry job functionality
    console.log('Retry job:', job.id);
    setActiveDropdown(null);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'running':
        return <Clock className="h-5 w-5 text-blue-500" />;
      case 'pending':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      running: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status as keyof typeof statusClasses] || 'bg-gray-100 text-gray-800'}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const formatDuration = (startedAt: string, completedAt?: string) => {
    if (!startedAt) return '-';
    
    const start = new Date(startedAt);
    const end = completedAt ? new Date(completedAt) : new Date();
    const duration = end.getTime() - start.getTime();
    
    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const columns = [
    {
      key: 'job',
      header: 'JOB',
      render: (value: any, row: Job) => (
        <div className="flex items-center">
          {getStatusIcon(row.status)}
          <div className="ml-3">
            <div className="font-medium text-gray-900">{row.name}</div>
            <div className="text-sm text-gray-500">ID: {row.id}</div>
          </div>
        </div>
      )
    },
    {
      key: 'status',
      header: 'STATUS',
      render: (value: any, row: Job) => getStatusBadge(row.status)
    },
    {
      key: 'created',
      header: 'CREATED',
      render: (value: any, row: Job) => (
        <div className="text-sm text-gray-900">
          {new Date(row.created_at).toLocaleString()}
        </div>
      )
    },
    {
      key: 'duration',
      header: 'DURATION',
      render: (value: any, row: Job) => (
        <div className="text-sm text-gray-900">
          {formatDuration(row.started_at || '', row.completed_at)}
        </div>
      )
    },
    {
      key: 'actions',
      header: 'ACTIONS',
      render: (value: any, row: Job) => (
        <div className="flex justify-center relative dropdown-container">
          <button 
            onClick={() => setActiveDropdown(activeDropdown === row.id ? null : row.id)}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <MoreVertical className="w-5 h-5" />
          </button>
          
          {activeDropdown === row.id && (
            <div className="absolute right-0 top-8 z-10 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[160px]">
              <button
                onClick={() => handleViewDetails(row)}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                View Details
              </button>
              {row.status === 'failed' && (
                <button
                  onClick={() => handleRetryJob(row)}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50"
                >
                  Retry Now
                </button>
              )}
            </div>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Background Jobs</h1>
        <p className="text-gray-600 mt-1">Monitor automated driver management tasks</p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        {/* Search Field - Full Width */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search jobs..."
              value={filters.search}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
        
        {/* Filter Dropdowns - Next Line */}
        <div className="flex justify-between items-end">
          <div className="flex-1 max-w-xs">
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="running">Running</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <button
            onClick={handleResetFilters}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* Jobs Table */}
      <Table
        columns={columns}
        data={jobs}
        isLoading={loading}
        pagination={pagination}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        onPageSizeChange={(pageSize) => setPagination(prev => ({ ...prev, page_size: pageSize, page: 1 }))}
        emptyMessage="No jobs found"
      />

      {/* Job Details Modal */}
      {showJobDetails && selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold">Job Details</h2>
              <button
                onClick={() => {
                  setShowJobDetails(false);
                  setSelectedJob(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Job Name</label>
                  <p className="text-sm text-gray-900">{selectedJob.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <div className="mt-1">{getStatusBadge(selectedJob.status)}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Created</label>
                  <p className="text-sm text-gray-900">{new Date(selectedJob.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Duration</label>
                  <p className="text-sm text-gray-900">{formatDuration(selectedJob.started_at || '', selectedJob.completed_at)}</p>
                </div>
              </div>

              {selectedJob.error_message && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Error Message</label>
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-800">{selectedJob.error_message}</p>
                  </div>
                </div>
              )}

              {selectedJob.result && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Result</label>
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                    <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                      {JSON.stringify(selectedJob.result, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
